import React, { useState, useEffect, useRef } from "react";
import ArrowIcon from '../../images/arrow_button_gurugaman.svg'
import "./GurugamanDigitalPrivacy.css";

const AccordionSection = ({
  title,
  children,
  isOpen,
  onToggle,
  sectionRef,
}) => {
  return (
    <div
      className={`gurugaman-accordion-section ${isOpen ? "open" : ""}`}
      ref={sectionRef}
    >
      <button
        className="gurugaman-accordion-header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <div className="gurugaman-accordion-icon-wrapper">
        <img src={ArrowIcon} alt="Arrow Icon" className={`gurugaman-accordion-icon ${isOpen ? "open" : ""}`}/>
        </div>
      </button>
      {isOpen && <div className="gurugaman-accordion-content">{children}</div>}
    </div>
  );
};

const DigitalPrivacyGurugaman = () => {
  const [showSkipButton, setShowSkipButton] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const [openSections, setOpenSections] = useState([]);

  // Refs for each accordion section
  const personalRef = useRef(null);
  const servicesRef = useRef(null);
  const rightsRef = useRef(null);

  const handleLinkClick = (section) => {
    setOpenSections((prev) =>
      prev.includes(section) ? prev : [...prev, section] // keep others open
    );
    setTimeout(() => {
      if (section === "personal" && personalRef.current) {
        personalRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "services" && servicesRef.current) {
        servicesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (section === "rights" && rightsRef.current) {
        rightsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };
  const configureConsentUI = (shouldShowSkip) => {
    try {
      setShowSkipButton(shouldShowSkip);
      return "Configuration applied successfully";
    } catch (error) {
      console.error("Error applying configuration:", error);
      return "Error applying configuration";
    }
  };

  const toggleSkipButton = (show) => setShowSkipButton(show);

  const notifyPlatform = (eventType) => {
    const payload = { message: `privacy_${eventType}` };
    const message = {
      action: eventType,
      data: {
        consent:
          eventType === "accept"
            ? true
            : eventType === "deny"
            ? false
            : "skipped",
        timestamp: new Date().toISOString(),
      },
      timestamp: new Date().toISOString(),
    };

    try {
      if (window.webkit?.messageHandlers?.nativeApp) {
        window.webkit.messageHandlers.nativeApp.postMessage(payload);
      }
      if (window.webkit?.messageHandlers?.consentHandler) {
        window.webkit.messageHandlers.consentHandler.postMessage(message);
      }
      if (
        eventType === "accept" &&
        window.webkit?.messageHandlers?.acceptClicked
      ) {
        window.webkit.messageHandlers.acceptClicked.postMessage(null);
      }
      if (eventType === "deny" && window.webkit?.messageHandlers?.denyClicked) {
        window.webkit.messageHandlers.denyClicked.postMessage(message);
      }
      if (eventType === "skip" && window.webkit?.messageHandlers?.skipClicked) {
        window.webkit.messageHandlers.skipClicked.postMessage(message);
      }

      if (typeof window.AndroidInterface !== "undefined") {
        if (eventType === "accept") window.AndroidInterface.acceptClicked();
        else if (eventType === "deny") window.AndroidInterface.denyClicked();
        else if (eventType === "skip") window.AndroidInterface.skipClicked();
      }

      if (
        typeof window.Android !== "undefined" &&
        window.Android.onConsentAction
      ) {
        window.Android.onConsentAction(JSON.stringify(message));
      } else if (typeof window.postMessage === "function") {
        window.postMessage(payload, "*");
      }

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.error(`Error during ${eventType} click`, error);
      alert(`Privacy policy ${eventType}`);
    }

    console.log("Privacy Consent Action:", message);
  };

  const handleAccept = () => {
    setShowLoader(true);
    notifyPlatform("accept");
  };
  const handleDeny = () => {
    setShowLoader(true);
    notifyPlatform("deny");
  };
  const handleSkip = () => {
    setShowLoader(true);
    notifyPlatform("skip");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.hideConsentLoader = () => setShowLoader(false);
      window.showConsentLoader = () => setShowLoader(true);
      window.configureConsentUI = configureConsentUI;
      window.toggleSkipButton = toggleSkipButton;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.hideConsentLoader;
        delete window.showConsentLoader;
        delete window.configureConsentUI;
        delete window.toggleSkipButton;
      }
    };
  }, []);

  const toggleSection = (section) => {
    setOpenSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section) // close if already open
        : [...prev, section] // open in addition
    );
  };

  return (
    <div className="privacy-container">
      <div className="gurugaman-privacy-modal-container">
        <h1 className="gurugaman-privacy-heading">
          We Value Your Privacy – Please Review & Accept
        </h1>
        <p className="gurugaman-privacy-desc">
          At Gurugaman, we need to collect your{" "}
          <span
            className="gurugaman-inline-link"
            onClick={() => handleLinkClick("personal")}
          >
            personal data
          </span>{" "}
          to provide{" "}
          <span
            className="gurugaman-inline-link"
            onClick={() => handleLinkClick("services")}
          >
            our services
          </span>
          , and you have the{" "}
          <span
            className="gurugaman-inline-link"
            onClick={() => handleLinkClick("rights")}
          >
            right
          </span>{" "}
          to manage it.
          <br />
          <br />
          Your privacy matters to us, so please take a moment to check our
          updated{" "}
          <a
            className="gurugaman-hyperlink-text"
            href="https://www.gmcbl.in/policies.html?language=en"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          . Without consent, access to the app will not be possible.
        </p>

        <div className="gurugaman-accordion-group">
          <AccordionSection
            title="Personal data examples"
            sectionRef={personalRef}
            isOpen={openSections.includes("personal")}
            onToggle={() => toggleSection("personal")}
          >
            <div className="gurugaman-privacy-info-box">
              <ul>
                <li>
                  <span className="gurugaman-privacy-info-title">Personal info:</span>{" "}
                  <span className="gurugaman-privacy-info-desc">
                    Name, phone number, gender, age, Aadhaar / student ID / disability certificate (as applicable).
                  </span>
                </li>
                <li>
                  <span className="gurugaman-privacy-info-title">App & travel:</span>{" "}
                  <span className="gurugaman-privacy-info-desc">
                    Pass ID, Gurugaman Smart Card, routes, stops, transaction and search history.
                  </span>
                </li>
                <li>
                  <span className="gurugaman-privacy-info-title">Financial:</span>{" "}
                  <span className="gurugaman-privacy-info-desc">
                    Payment details, card / wallet balance.
                  </span>
                </li>
                <li>
                  <span className="gurugaman-privacy-info-title">Location & device:</span>{" "}
                  <span className="gurugaman-privacy-info-desc">
                    Coordinates, device ID, carrier, WiFi, screen size, app version.
                  </span>
                </li>
                <li>
                  <span className="gurugaman-privacy-info-title">System:</span>{" "}
                  <span className="gurugaman-privacy-info-desc">
                    Language preference, feature usage and performance analytics.
                  </span>
                </li>
              </ul>
            </div>
          </AccordionSection>

          <AccordionSection
            title="Our services"
            sectionRef={servicesRef}
            isOpen={openSections.includes("services")}
            onToggle={() => toggleSection("services")}
          >
            <p className="gurugaman-privacy-desc">
              Ticketing, live tracking, enhanced user experience, Gurugaman / third-party product / services' promotions, security, compliance, research and analytics. The app is operated by GMCBL and powered by its technology partner, Chalo.
            </p>
          </AccordionSection>

          <AccordionSection
            title="Your rights"
            sectionRef={rightsRef}
            isOpen={openSections.includes("rights")}
            onToggle={() => toggleSection("rights")}
          >
            <p className="gurugaman-privacy-desc">
              You can access, update, correct or delete your data, or withdraw consent any time. For assistance, contact us at travelintimegmcbl@gmail.com or the Gurugaman helpline 7303422556 / toll-free 18001806627.
            </p>
          </AccordionSection>
        </div>

        <div className="gurugaman-privacy-btn-group">
          <div className="gurugaman-privacy-consent">
            I am above 18 years, and have read, understood, and consent to Gurugaman's privacy policy.
            My child is below 18 years, and I as the parent have read, understood, and consented to Gurugaman's privacy policy.
          </div>
          <button className="gurugaman-privacy-btn accept" onClick={handleAccept}>
            Accept
          </button>
          <button className="gurugaman-privacy-btn deny" onClick={handleDeny}>
            Deny
          </button>
          <button
            className={`gurugaman-privacy-btn skip ${showSkipButton ? "" : "gurugaman-hidden"}`}
            onClick={handleSkip}
          >
            I will do this later
          </button>
        </div>
      </div>

      {showLoader && (
        <div className="gurugaman-fullscreen-loader">
          <div className="gurugaman-loader-container">
            <div className="gurugaman-loader-spinner"></div>
            <div className="gurugaman-loader-text">Loading...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalPrivacyGurugaman;
